import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  InputBase,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import SectionHeader from './SectionHeader'
import StatusPill from './StatusPill'
import type { StudyStatus } from './dashboardTypes'
import { FA_COLOR } from './liveDemo/constants'
import type { ProjectEmotionSummary } from '@/services/emotionAnalysisService'

export interface StudyRow {
  id: number
  name: string
  client: string
  status: StudyStatus
  responses: { n: number; t: number }
  createdLabel: string
  lastActivityLabel: string
  /** Whole-study emotion summary; undefined while still loading, null when absent. */
  emotion?: ProjectEmotionSummary | null
  /** Shared with the whole team rather than kept to its author. */
  sharedWithTeam?: boolean
  /** Author, shown only on studies the signed-in researcher did not create. */
  authorLabel?: string
}

type FilterKey = 'all' | 'active' | 'completed' | 'drafts'

/** Second axis, only meaningful inside a team: whose studies am I looking at. */
type ScopeKey = 'mine' | 'team' | 'all'

const SCOPE_MATCH: Record<ScopeKey, (r: StudyRow) => boolean> = {
  mine: (r) => !r.authorLabel,
  team: (r) => Boolean(r.authorLabel) || Boolean(r.sharedWithTeam),
  all: () => true,
}

const FILTER_MATCH: Record<FilterKey, (s: StudyStatus) => boolean> = {
  all: () => true,
  active: (s) => s === 'active' || s === 'paused',
  completed: (s) => s === 'done' || s === 'review',
  drafts: (s) => s === 'draft',
}

const ACTION_BY_STATUS: Record<StudyStatus, 'monitor' | 'viewResults' | 'editDraft' | 'openReport'> = {
  active: 'monitor',
  paused: 'monitor',
  danger: 'monitor',
  review: 'viewResults',
  done: 'openReport',
  draft: 'editDraft',
}

function ResponsesBar({ n, t }: { n: number; t: number }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const pct = t > 0 ? Math.min(100, (n / t) * 100) : 0
  return (
    <Stack spacing={0.5} sx={{ width: '100%', minWidth: 96 }}>
      <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
        {n} <Box component="span" sx={{ color: c.ink56 }}>/ {t}</Box>
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 4,
          borderRadius: 34,
          bgcolor: c.gray100,
          '& .MuiLinearProgress-bar': { bgcolor: pct >= 100 ? c.success : c.coral },
        }}
      />
    </Stack>
  )
}

/** Reactivity column: share of non-neutral emotion as a value + mini bar. */
function ReactivityCell({ emotion }: { emotion: StudyRow['emotion'] }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  if (emotion === undefined) return <Box sx={{ width: 80, height: 4, borderRadius: 34, bgcolor: c.gray100 }} />
  if (!emotion?.has_data) return <Typography sx={{ color: c.ink48 }}>—</Typography>
  const r = emotion.reactivity
  const bandColor = r >= 0.45 ? c.success : r >= 0.2 ? c.coral : c.ink48
  return (
    <Stack spacing={0.5} sx={{ width: '100%', minWidth: 84 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: bandColor, fontVariantNumeric: 'tabular-nums' }}>
        {Math.round(r * 100)}%
      </Typography>
      <Box sx={{ height: 4, borderRadius: 34, bgcolor: c.gray100, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${Math.round(r * 100)}%`, bgcolor: bandColor }} />
      </Box>
    </Stack>
  )
}

/** Dominant-emotion column: the strongest non-neutral emotion as a colour chip. */
function DominantEmotionCell({ emotion }: { emotion: StudyRow['emotion'] }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t: tEmo } = useTranslation('liveDemo')
  if (emotion === undefined) return <Box sx={{ width: 64, height: 18, borderRadius: 34, bgcolor: c.gray100 }} />
  if (!emotion?.has_data || !emotion.dominant) return <Typography sx={{ color: c.ink48 }}>—</Typography>
  const color = FA_COLOR[emotion.dominant]
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '3px', background: color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{tEmo(`emotions.${emotion.dominant}`)}</Typography>
      <Typography sx={{ fontSize: 12, color: c.ink48, fontVariantNumeric: 'tabular-nums' }}>
        {Math.round(emotion.dominant_share * 100)}%
      </Typography>
    </Box>
  )
}

type StudiesTableProps = {
  rows: StudyRow[]
  counts: Record<FilterKey, number>
  /** Show the Mine / Team / All chips — pass true only for team accounts. */
  showScope?: boolean
  onRowAction: (row: StudyRow) => void
  /** Override the card title/subtitle (defaults to the dashboard "studies" copy). */
  title?: string
  subtitle?: string
}

export default function StudiesTable({ rows, counts, onRowAction, title, subtitle, showScope = false }: StudiesTableProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const { t: tTeam } = useTranslation('team')
  const [filter, setFilter] = useState<FilterKey>('all')
  const [scope, setScope] = useState<ScopeKey>('all')
  const [query, setQuery] = useState('')

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rows.filter(
      (r) =>
        FILTER_MATCH[filter](r.status) &&
        (!showScope || SCOPE_MATCH[scope](r)) &&
        (!q || r.name.toLowerCase().includes(q)),
    )
  }, [rows, filter, scope, showScope, query])

  const headCellSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: c.ink48,
    borderBottom: `1px solid ${c.ink08}`,
    py: 1,
  }
  const bodyCellSx = {
    fontSize: 13.5,
    borderBottom: `1px solid ${c.ink08}`,
    py: 1.75,
  }

  const filters: FilterKey[] = ['all', 'active', 'completed', 'drafts']

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.75, md: 2.5 },
        borderRadius: `${theme.affexy.radii.lg / 1.33}px`,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
      }}
    >
      <SectionHeader
        title={title ?? t('studies.title')}
        subtitle={subtitle ?? t('studies.subtitle')}
        right={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              bgcolor: c.gray50,
              borderRadius: 34,
              width: { xs: '100%', sm: 220 },
              maxWidth: '100%',
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: c.ink48 }} />
            <InputBase
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('studies.searchPlaceholder')}
              sx={{ flex: 1, fontSize: 12.5 }}
            />
          </Box>
        }
      />

      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {filters.map((f) => {
          const active = filter === f
          return (
            <Chip
              key={f}
              onClick={() => setFilter(f)}
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  {t(`studies.filter.${f}`)}
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      borderRadius: 34,
                      bgcolor: active ? 'rgba(255,255,255,.2)' : c.gray100,
                      color: active ? '#fff' : c.ink56,
                      fontSize: 11,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {counts[f]}
                  </Box>
                </Box>
              }
              variant={active ? 'filled' : 'outlined'}
              sx={{
                height: 32,
                bgcolor: active ? c.ink : '#fff',
                color: active ? '#fff' : c.ink72,
                borderColor: active ? c.ink : c.ink08,
                '&:hover': { bgcolor: active ? c.ink : c.gray50 },
              }}
            />
          )
        })}

        {showScope && (
          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', ml: { sm: 'auto' } }}>
            {(['mine', 'team', 'all'] as ScopeKey[]).map((s) => {
              const active = scope === s
              return (
                <Chip
                  key={s}
                  onClick={() => setScope(s)}
                  label={tTeam(`visibility.filter.${s}`)}
                  variant={active ? 'filled' : 'outlined'}
                  sx={{
                    height: 32,
                    bgcolor: active ? alpha(c.coral, 0.1) : '#fff',
                    color: active ? c.coralHover : c.ink72,
                    borderColor: active ? alpha(c.coral, 0.35) : c.ink08,
                    fontWeight: 600,
                    '&:hover': { bgcolor: active ? alpha(c.coral, 0.14) : c.gray50 },
                  }}
                />
              )
            })}
          </Stack>
        )}
      </Stack>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760, '& td, & th': { px: 0, pr: 2 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>{t('studies.column.name')}</TableCell>
              <TableCell sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{t('studies.column.client')}</TableCell>
              <TableCell sx={headCellSx}>{t('studies.column.status')}</TableCell>
              <TableCell sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{t('studies.column.responses')}</TableCell>
              <TableCell sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{t('studies.column.reactivity')}</TableCell>
              <TableCell sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{t('studies.column.dominantEmotion')}</TableCell>
              <TableCell sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>{t('studies.column.created')}</TableCell>
              <TableCell sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>{t('studies.column.lastActivity')}</TableCell>
              <TableCell sx={headCellSx} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((r) => {
              const actionKey = ACTION_BY_STATUS[r.status]
              const emphasised = r.status === 'review' || r.status === 'done'
              return (
                <TableRow
                  key={r.id}
                  hover
                  onClick={() => onRowAction(r)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: c.ink }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      {r.name}
                      {r.sharedWithTeam && (
                        <Chip
                          size="small"
                          label={tTeam('visibility.chip')}
                          sx={{
                            height: 20,
                            fontSize: 10.5,
                            fontWeight: 700,
                            bgcolor: alpha(c.coral, 0.09),
                            color: c.coralHover,
                          }}
                        />
                      )}
                    </Box>
                    {r.authorLabel && (
                      <Box sx={{ fontSize: 11.5, fontWeight: 400, color: c.ink48, mt: 0.25 }}>
                        {tTeam('visibility.author')}: {r.authorLabel}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: r.client ? c.ink : c.ink56, display: { xs: 'none', md: 'table-cell' } }}>
                    {r.client || t('studies.internalClient')}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <StatusPill kind={r.status} />
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', sm: 'table-cell' } }}>
                    <ResponsesBar n={r.responses.n} t={r.responses.t} />
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', md: 'table-cell' } }}>
                    <ReactivityCell emotion={r.emotion} />
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', md: 'table-cell' } }}>
                    <DominantEmotionCell emotion={r.emotion} />
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: c.ink56, fontVariantNumeric: 'tabular-nums', display: { xs: 'none', lg: 'table-cell' } }}>
                    {r.createdLabel}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: c.ink56, display: { xs: 'none', lg: 'table-cell' } }}>{r.lastActivityLabel}</TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    <Button
                      variant={emphasised ? 'contained' : 'outlined'}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        onRowAction(r)
                      }}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {t(`studies.action.${actionKey}`)}
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
